import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Header from "./components/Header";
import Footer from "./components/Footer";
import WhatsAppFloat from "./components/WhatsAppFloat";
import Home from "./pages/Home";
import About from "./pages/About";
import Services from "./pages/Services";
import AuditDefense from "./pages/AuditDefense";
import FAQ from "./pages/FAQ";
import Contact from "./pages/Contact";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import TaxAmendment from "./pages/TaxAmendment";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import ResetPassword from "./pages/ResetPassword";
import IrsNews from "./pages/IrsNews";
import Resources from "./pages/Resources";

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { err: false };
    }
    static getDerivedStateFromError() {
        return { err: true };
    }
    componentDidCatch(error) {
        console.error(error);
    }
    render() {
        if (this.state.err) {
            return (
                <div className="min-h-screen bg-[#111318] text-white flex items-center justify-center p-8 text-center">
                    <p className="font-display text-2xl">Something went wrong. Please refresh the page.</p>
                </div>
            );
        }
        return this.props.children;
    }
}

function ScrollManager() {
    const { pathname, hash } = useLocation();
    React.useEffect(() => {
        if (hash) {
            const id = hash.replace("#", "");
            let tries = 0;
            const jump = () => {
                const el = document.getElementById(id);
                if (el) {
                    const y = el.getBoundingClientRect().top + window.scrollY - 112;
                    if (window.__lenis) window.__lenis.scrollTo(y, { immediate: true });
                    else window.scrollTo(0, y);
                } else if (tries++ < 30) {
                    requestAnimationFrame(jump);
                }
            };
            requestAnimationFrame(jump);
            return;
        }
        if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
        window.scrollTo(0, 0);
    }, [pathname, hash]);
    return null;
}

function ProtectedRoute({ children }) {
    const { user } = useAuth();
    const location = useLocation();
    if (user === null) return <div className="min-h-[60vh] bg-[#FAFAF7]" />;
    if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
    return children;
}

function AnimatedRoutes() {
    const location = useLocation();
    return (
        <motion.main
            key={location.pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
            <Routes location={location}>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/services" element={<Services />} />
                <Route path="/audit-defense" element={<AuditDefense />} />
                <Route path="/tax-amendment" element={<TaxAmendment />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/login" element={<Login />} />
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/irs-news" element={<IrsNews />} />
                <Route path="/resources" element={<Resources />} />
                <Route path="*" element={<Home />} />
            </Routes>
        </motion.main>
    );
}

function App() {
    React.useEffect(() => {
        const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (reduced) return undefined;
        let lenis;
        let raf;
        try {
            const Lenis = require("lenis").default;
            lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
            window.__lenis = lenis;
            const loop = (t) => {
                lenis.raf(t);
                raf = requestAnimationFrame(loop);
            };
            raf = requestAnimationFrame(loop);
        } catch (e) {
            console.error("Lenis init failed", e);
        }
        return () => {
            if (raf) cancelAnimationFrame(raf);
            if (lenis) lenis.destroy();
            window.__lenis = null;
        };
    }, []);

    return (
        <ErrorBoundary>
            <AuthProvider>
                <BrowserRouter>
                    <ScrollManager />
                    <Header />
                    <AnimatedRoutes />
                    <Footer />
                </BrowserRouter>
                <Toaster position="bottom-right" offset={88} />
                <WhatsAppFloat />
            </AuthProvider>
        </ErrorBoundary>
    );
}

export default App;
